/**
 * 파이프라인 — 이 앱이 실제로 하는 일의 목차.
 *
 *   Blob ─decode→ Raster ─analyze→ {실루엣, 거리장, 팔레트, 통계}
 *        ─depth→ 깊이맵 ─mesh→ 지오메트리
 *        ─pbr→ {albedo, normal, ORM, emissive} ─material→ 머티리얼
 *
 * 단계마다 결과를 들고 있는 이유는 재계산 범위를 좁히기 위해서다.
 * 러프니스 슬라이더를 건드렸다고 신경망을 다시 돌릴 이유는 없다.
 */

import type {
  DepthBackend,
  ForgeParams,
  Field,
  ImageAnalysis,
  MaterialMaps,
  PresetId,
  Raster,
} from './types.ts';
import { analyzeImage } from './imaging/analyze.ts';
import { decodeImage, gaussianBlur, luminanceField, makeRaster, resampleField } from './imaging/raster.ts';
import { distanceTransform } from './imaging/silhouette.ts';
import type { ViewImage } from './turnaround/views.ts';
import { estimateDepth, type DepthOutcome, type DepthProgress } from './depth/index.ts';
import { buildMaterialMaps } from './pbr/maps.ts';
import { buildMaterial, type ForgedMaterial } from './pbr/material.ts';
import { buildMesh, type ForgedMesh } from './mesh/build.ts';
import { resolveParams } from './presets.ts';
import { splitSheet, type SheetSplit } from './sheet/split.ts';
import { buildTurnaroundHull, type HullMesh } from './turnaround/hull.ts';
import { buildAtlas, type ViewAtlas } from './turnaround/atlas.ts';
import { azimuthsFromNames, fromRasters, fromSheet, type TurnaroundSet } from './turnaround/views.ts';

export interface StageReport {
  stage: 'decode' | 'analyze' | 'depth' | 'maps' | 'mesh' | 'material';
  label: string;
  detail?: string;
  ratio?: number;
}

export interface PipelineOptions {
  backend: DepthBackend;
  anisotropy?: number;
  onStage?: (r: StageReport) => void;
  signal?: AbortSignal;
}

export interface ForgeOutput {
  analysis: ImageAnalysis;
  depth: DepthOutcome;
  maps: MaterialMaps;
  mesh: ForgedMesh;
  material: ForgedMaterial;
  params: ForgeParams;
  effectivePreset: PresetId;
  timings: Record<string, number>;
}

const now = () => performance.now();

export async function decodeAndAnalyze(
  blob: Blob,
  maxSize: number,
  onStage?: (r: StageReport) => void,
): Promise<{ raster: Raster; analysis: ImageAnalysis; timings: Record<string, number> }> {
  const timings: Record<string, number> = {};

  onStage?.({ stage: 'decode', label: '이미지 읽는 중' });
  let t = now();
  const raster = await decodeImage(blob, maxSize);
  timings.decode = now() - t;

  onStage?.({ stage: 'analyze', label: '실루엣과 팔레트 분석 중' });
  t = now();
  const analysis = analyzeImage(raster);
  timings.analyze = now() - t;

  return { raster, analysis, timings };
}

export async function runDepth(
  analysis: ImageAnalysis,
  opts: PipelineOptions,
): Promise<DepthOutcome> {
  const label = opts.backend === 'onnx' ? '신경망 깊이 추정 중' : '깊이 추정 중';
  opts.onStage?.({ stage: 'depth', label });
  return estimateDepth(analysis, {
    backend: opts.backend,
    signal: opts.signal,
    onProgress: (p: DepthProgress) => {
      opts.onStage?.({
        stage: 'depth',
        label,
        detail: describeProgress(p),
        ratio: p.ratio,
      });
    },
  });
}

function describeProgress(p: DepthProgress): string {
  switch (p.phase) {
    case 'initiate':
    case 'download':
    case 'progress':
      return `모델 내려받는 중 ${p.file ?? ''}`.trim();
    case 'done':
      return '모델 준비됨';
    case 'inference':
      return '추론 중';
    case 'webgpu-fallback':
      return 'WebGPU 실패 — WASM으로 전환';
    default:
      return p.phase;
  }
}

/**
 * 깊이맵이 이미 있을 때 뒤쪽만 다시 돈다. 슬라이더 조작의 기본 경로.
 * 머티리얼은 여기서 만들지 않는다 — 앱 수명 동안 하나뿐이고 텍스처만 갈아 끼운다
 * (material.ts 머리말 참고). 호출부가 `updateMaterialMaps` 로 마무리한다.
 */
export function buildFromDepth(
  analysis: ImageAnalysis,
  depth: DepthOutcome,
  params: ForgeParams,
  opts: Pick<PipelineOptions, 'onStage'>,
  reuse?: { maps?: MaterialMaps },
): { maps: MaterialMaps; mesh: ForgedMesh; timings: Record<string, number> } {
  const timings: Record<string, number> = {};

  let t = now();
  let maps = reuse?.maps;
  if (!maps) {
    opts.onStage?.({ stage: 'maps', label: 'PBR 맵 굽는 중' });
    maps = buildMaterialMaps(analysis, params);
  }
  timings.maps = now() - t;

  opts.onStage?.({ stage: 'mesh', label: '메시 만드는 중' });
  t = now();
  const mesh = buildMesh(analysis, depth.field, params);
  timings.mesh = now() - t;

  return { maps, mesh, timings };
}

/** 처음부터 끝까지. 파일을 새로 떨어뜨렸을 때 쓴다. */
export async function forgeFromBlob(
  blob: Blob,
  preset: PresetId,
  overrides: Partial<ForgeParams>,
  opts: PipelineOptions & { maxSize?: number },
): Promise<ForgeOutput> {
  const { analysis, timings } = await decodeAndAnalyze(blob, opts.maxSize ?? 1024, opts.onStage);
  const { params, effective } = resolveParams(preset, analysis.stats, overrides);

  const depth = await runDepth(analysis, opts);
  timings.depth = depth.ms;

  const built = buildFromDepth(analysis, depth, params, opts);
  Object.assign(timings, built.timings);

  opts.onStage?.({ stage: 'material', label: '머티리얼 조립 중' });
  const t = now();
  const material = buildMaterial(built.maps, params, { anisotropy: opts.anisotropy });
  timings.material = now() - t;

  return {
    analysis,
    depth,
    maps: built.maps,
    mesh: built.mesh,
    material,
    params,
    effectivePreset: effective,
    timings,
  };
}

// ---------------------------------------------------------------------------
// 턴어라운드(N뷰) 경로 — 3뷰 시트 한 장, 또는 뷰 이미지 여러 장
// ---------------------------------------------------------------------------

export interface TurnaroundInput {
  set: TurnaroundSet;
  atlas: ViewAtlas;
  /** 시트에서 왔다면 분할 결과(패널 검출 여부 표시용) */
  split: SheetSplit | null;
}

/** 시트 한 장 → 뷰 세트 + 아틀라스 + 아틀라스 기준 이미지 분석. */
export async function decodeSheet(
  blob: Blob,
  onStage?: (r: StageReport) => void,
): Promise<{ input: TurnaroundInput; analysis: ImageAnalysis; timings: Record<string, number> }> {
  const timings: Record<string, number> = {};
  onStage?.({ stage: 'decode', label: '시트 읽는 중' });
  let t = now();
  const raster = await decodeImage(blob, 1536);
  timings.decode = now() - t;

  onStage?.({ stage: 'analyze', label: '세 뷰로 나누는 중' });
  t = now();
  const split = splitSheet(raster);
  const set = fromSheet(split);
  return finishTurnaround({ set, split }, timings, t);
}

/** 뷰 이미지 여러 장 → 장수에 따라 방위 배정. */
export async function decodeViews(
  blobs: Blob[],
  onStage?: (r: StageReport) => void,
  azimuths?: number[],
): Promise<{ input: TurnaroundInput; analysis: ImageAnalysis; timings: Record<string, number> }> {
  const timings: Record<string, number> = {};
  onStage?.({ stage: 'decode', label: `뷰 ${blobs.length}장 읽는 중` });
  let t = now();
  const rasters = await Promise.all(blobs.map((b) => decodeImage(b, 1024)));
  timings.decode = now() - t;
  onStage?.({ stage: 'analyze', label: '뷰 실루엣 분석 중' });
  t = now();
  // 방위: 인자 > 파일 이름(view-045) > 장수 배정.
  const named = azimuthsFromNames(blobs.map((b) => (b instanceof File ? b.name : '')));
  const set = fromRasters(rasters, azimuths ? 'api' : 'files', azimuths ?? named);
  return finishTurnaround({ set, split: null }, timings, t);
}

function finishTurnaround(
  partial: { set: TurnaroundSet; split: SheetSplit | null },
  timings: Record<string, number>,
  t0: number,
) {
  const atlas = buildAtlas(partial.set);
  // PBR 맵은 아틀라스에서 굽는다 — 셰이더도, 대표 UV 도 아틀라스 좌표를 쓴다.
  const analysis = analyzeImage(atlas.raster, { mask: atlas.mask });
  timings.analyze = now() - t0;
  return { input: { ...partial, atlas }, analysis, timings };
}

export function buildFromTurnaround(
  input: TurnaroundInput,
  params: ForgeParams,
  opts: Pick<PipelineOptions, 'onStage'>,
  depths?: (Field | null)[],
): { mesh: HullMesh; timings: Record<string, number> } {
  const carving = params.hullCarve > 0 && depths?.some(Boolean);
  opts.onStage?.({ stage: 'mesh', label: `비주얼 헐 깎는 중 (${input.set.views.length}뷰${carving ? ' + 깊이' : ''})` });
  const t = now();
  const mesh = buildTurnaroundHull(input.set, input.atlas, {
    resolution: params.hullResolution,
    smooth: params.hullSmooth,
    sideFacesLeft: params.sideFacesLeft,
    blur: 1,
    relief: params.hullRelief,
    carve: params.hullCarve,
    // 관용은 뷰가 6장 이상일 때만 뜻이 있다 — 3~4뷰에서 하나를 무시하면 헐이 상자가 된다.
    tolerance: input.set.views.length >= 6 ? params.hullTolerance : 0,
    dilate: params.hullDilate,
    depthRelief: params.hullDepthRelief,
    depths,
  });
  return { mesh, timings: { mesh: now() - t } };
}

/** 깊이 추정용 경량 분석 — 팔레트·통계 없이, 긴 변 maxDim 으로 줄여서. */
function depthAnalysisOf(view: ViewImage, maxDim: number): ImageAnalysis {
  const { raster, mask } = view;
  const scale = Math.min(1, maxDim / Math.max(raster.width, raster.height));
  const w = Math.max(8, Math.round(raster.width * scale)), h = Math.max(8, Math.round(raster.height * scale));
  const src = makeRaster(w, h);
  const inv = 1 / scale;
  for (let y = 0; y < h; y++) {
    const sy = Math.min(raster.height - 1, Math.floor((y + 0.5) * inv));
    for (let x = 0; x < w; x++) {
      const sx = Math.min(raster.width - 1, Math.floor((x + 0.5) * inv));
      const si = (sy * raster.width + sx) * 4, di = (y * w + x) * 4;
      src.data[di] = raster.data[si]!; src.data[di + 1] = raster.data[si + 1]!; src.data[di + 2] = raster.data[si + 2]!; src.data[di + 3] = raster.data[si + 3]!;
    }
  }
  const m = scale < 1 ? resampleField(mask, w, h) : mask;
  const distance = gaussianBlur(distanceTransform(m), Math.max(1, Math.max(w, h) / 250));
  return {
    source: src,
    mask: m,
    distance,
    luma: luminanceField(src),
    palette: [],
    stats: { meanLuma: 0, contrast: 0, meanSaturation: 0, detail: 0, specularRatio: 0, shadowRatio: 0 },
    coverage: 0,
    maskSource: 'flood',
  };
}

/**
 * 뷰별 단안 깊이. 깊이 깎기(hullCarve)가 쓴다. 뷰 이미지에 대해 단일 뷰 경로와 같은
 * 추정기(휴리스틱 / Depth-Anything)를 돌린다 — 결과는 캐시해 두는 것이 호출부 책임이다.
 */
export async function estimateViewDepths(
  input: TurnaroundInput,
  opts: PipelineOptions,
): Promise<(Field | null)[]> {
  const out: (Field | null)[] = [];
  const n = input.set.views.length;
  for (let i = 0; i < n; i++) {
    const v = input.set.views[i]!;
    opts.onStage?.({ stage: 'depth', label: `뷰 깊이 추정 중 ${i + 1}/${n}${opts.backend === 'onnx' ? ' (신경망)' : ''}` });
    try {
      // 깊이 추정기는 ImageAnalysis 를 받지만 source·mask·distance·luma 만 본다.
      // 깎기 맵은 192² 라 512 로 줄여서 돈다 — 1024² 여덟 장을 그대로 돌리면 메인 스레드가 수십 초 멈춘다(실측).
      const analysis = depthAnalysisOf(v, 512);
      const d = await estimateDepth(analysis, {
        backend: opts.backend,
        forCarve: true,
        signal: opts.signal,
        onProgress: (p: DepthProgress) => opts.onStage?.({ stage: 'depth', label: `뷰 깊이 추정 중 ${i + 1}/${n}`, detail: describeProgress(p), ratio: p.ratio }),
      });
      out.push(d.field);
    } catch {
      out.push(null);
    }
  }
  return out;
}
